import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";

const CAT = { MIX_UNIDENTIFIED: "MIX / unidentified", UNKNOWN_SKU: "Unknown SKU", MISSING_IDENTIFIER: "Missing identifier", INVALID_QUANTITY: "Invalid quantity" };
const STATUS = { UNRESOLVED: "badge-neutral", RESOLVED: "badge-available", EXCLUDED: "badge-sold", PROCESSING: "badge-neutral" };
const when = (s) => (s ? new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

// ID-086: shared warning — rows and known boxes are counted separately from product lines
export const ExceptionWarning = ({ rows, boxes, onReview, testid }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-md border border-terracotta/40 bg-terracotta/5 p-3 text-sm text-charcoal" data-testid={testid}>
    <AlertTriangle className="h-4 w-4 shrink-0 text-terracotta" />
    <span className="flex-1">{rows} unresolved import row{rows === 1 ? "" : "s"} · {boxes} known box{boxes === 1 ? "" : "es"} not yet in inventory</span>
    {onReview && <Button size="sm" variant="outline" onClick={onReview} data-testid={`${testid}-review`}>Review issues</Button>}
  </div>
);

const AssignDialog = ({ e, onClose, onDone }) => {
  const [sku, setSku] = useState("");
  const [boxes, setBoxes] = useState("");
  const [prop, setProp] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (e) { setSku(""); setBoxes(e.boxes ?? ""); setProp(null); } }, [e]);
  if (!e) return null;
  const n = Number(boxes);
  const valid = sku.trim() && Number.isInteger(n) && n > 0;
  const go = async (confirm) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/container-import-exceptions/${e.id}/assign-sku`, { sku: sku.trim(), boxes: n, confirm });
      if (confirm) { toast.success(`Row ${e.source_row} reprocessed as ${data.resolution.sku}`); onDone(); } else setProp(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setProp(null); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-border bg-white sm:max-w-md" data-testid="exception-assign-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl text-charcoal">Assign SKU · row {e.source_row}</DialogTitle>
          <DialogDescription className="text-xs text-warmgrey">Supplier gave “{e.sku_text || "—"}” · quantity “{e.boxes_raw ?? "—"}”. These original values stay on record.</DialogDescription></DialogHeader>
        <Input value={sku} onChange={(x) => { setSku(x.target.value); setProp(null); }} placeholder="Approved SKU, e.g. MZ004" className="h-10 bg-white" data-testid="exception-sku-input" />
        <Input type="number" min="1" step="1" value={boxes} onChange={(x) => { setBoxes(x.target.value); setProp(null); }} placeholder="Boxes (whole number)" className="h-10 bg-white" data-testid="exception-boxes-input" />
        {!valid && boxes !== "" && <p className="text-xs text-status-sold">Enter an approved SKU and a whole number of boxes above 0.</p>}
        {prop && <p className="rounded-md border border-border bg-sand/40 p-2 text-xs text-charcoal" data-testid="exception-proposal">{prop.variant_label} ({prop.sku}) · {prop.boxes} boxes → {prop.container}. {prop.effect}</p>}
        <Button disabled={!valid || busy} onClick={() => go(!!prop)} className="h-10 bg-terracotta text-white hover:bg-terracotta-hover" data-testid="exception-assign-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : prop ? "Confirm and reimport correction" : "Review correction"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const ExcludeDialog = ({ e, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!e) return null;
  const go = async () => {
    setBusy(true);
    try { await api.post(`/container-import-exceptions/${e.id}/exclude`, { reason }); toast.success(`Row ${e.source_row} excluded`); onDone(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-border bg-white sm:max-w-md" data-testid="exception-exclude-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl text-charcoal">Exclude row {e.source_row}</DialogTitle>
          <DialogDescription className="text-xs text-warmgrey">“{e.sku_text || "—"}” · {e.boxes_raw ?? "—"} boxes. No inventory is created, and an exclusion does not mean these boxes are accounted for.</DialogDescription></DialogHeader>
        <Textarea value={reason} onChange={(x) => setReason(x.target.value)} placeholder="Reason (required)" className="bg-white" data-testid="exception-exclude-reason" />
        <Button disabled={reason.trim().length < 5 || busy} onClick={go} className="h-10 bg-charcoal text-white" data-testid="exception-exclude-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Approve exclusion"}</Button>
      </DialogContent>
    </Dialog>
  );
};

export const ExceptionRow = ({ e, onAssign, onExclude, extra }) => (
  <li className="border-t border-border py-3 text-sm" data-testid={`exception-${e.source_row}`}>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-charcoal"><span className="font-mono text-xs text-warmgrey">{e.filename} · row {e.source_row}</span> · {CAT[e.category] || e.category}
          <span className={`badge-status ml-2 ${STATUS[e.status]}`} data-testid={`exception-status-${e.source_row}`}>{e.status.toLowerCase()}</span></p>
        <p className="mt-1 text-xs text-warmgrey">SKU text “{e.sku_text || "—"}” · description {e.description || "—"} · quantity as supplied “{e.boxes_raw ?? "—"}” · parsed {e.boxes ?? "not valid"}</p>
        <p className="text-xs text-warmgrey">{e.explanation} · created {when(e.created_at)} by {e.created_by_name || e.created_by}</p>
        {e.resolution && <p className="text-xs text-charcoal" data-testid={`exception-resolution-${e.source_row}`}>Reimported as {e.resolution.sku} · {e.resolution.boxes} boxes · {when(e.resolution.at)}</p>}
        {e.exclusion && <p className="text-xs text-charcoal" data-testid={`exception-exclusion-${e.source_row}`}>Excluded: “{e.exclusion.reason}” · approved {when(e.exclusion.approved_at)} by {e.exclusion.by_name || e.exclusion.approved_by} · boxes not accounted for</p>}
        {(e.history || []).length > 0 && <ul className="mt-1 text-[11px] text-warmgrey" data-testid={`exception-history-${e.source_row}`}>{e.history.map((h, i) => <li key={i}>{when(h.at)} · {h.action.replace(/_/g, " ").toLowerCase()}{h.sku ? ` ${h.sku}` : ""}{h.boxes ? ` · ${h.boxes} boxes` : ""}{h.reason ? ` · “${h.reason}”` : ""}{h.result && h.result !== "OK" ? ` · ${h.result}` : ""} · {h.by_name || h.by}</li>)}</ul>}
      </div>
      {e.status === "UNRESOLVED" && <div className="flex gap-2">{extra}
        {onAssign && <Button size="sm" variant="outline" onClick={() => onAssign(e)} data-testid={`exception-assign-${e.source_row}`}>Assign SKU</Button>}
        <Button size="sm" variant="outline" onClick={() => onExclude(e)} data-testid={`exception-exclude-${e.source_row}`}>Exclude</Button></div>}
    </div>
  </li>
);

// ID-086: review + reconcile one container's import exceptions inside Container detail
export const ContainerExceptions = ({ containerId, containerStatus, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [assign, setAssign] = useState(null);
  const [excl, setExcl] = useState(null);
  const load = () => api.get(`/containers/${containerId}/import-exceptions`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [containerId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!rows.length || window.location.hash !== "#import-exceptions") { return undefined; }
    const t = setTimeout(() => document.getElementById("import-exceptions")?.scrollIntoView({ behavior: "smooth", block: "start" }), 900);
    return () => clearTimeout(t);
  }, [rows.length]);
  if (!rows.length) return null;
  const open = rows.filter((r) => r.status === "UNRESOLVED");
  const received = containerStatus === "RECEIVED";
  const shown = showAll ? rows : open;
  const done = () => { setAssign(null); setExcl(null); load(); onChanged?.(); };
  return (
    <section id="import-exceptions" className="mt-8 rounded-md border border-border bg-white p-5" data-testid="container-exceptions">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-2xl font-medium text-charcoal">Import issues</h2>
        <button className="text-xs text-terracotta underline" onClick={() => setShowAll((x) => !x)} data-testid="exceptions-toggle-all">{showAll ? "Show unresolved only" : `Show all (${rows.length})`}</button>
      </div>
      {open.length > 0 && <div className="mt-3"><ExceptionWarning rows={open.length} boxes={open.reduce((a, r) => a + (r.boxes || 0), 0)} testid="container-exceptions-warning" /></div>}
      {received && open.length > 0 && <p className="mt-3 rounded-md border border-border bg-cream/60 p-3 text-xs text-charcoal" data-testid="exceptions-received-note">This container is already received in Florida — import-issue reconciliation can no longer change stock. Use Inventory → Adjust inventory (audited adjustment with reason) for any physical correction.</p>}
      {shown.length === 0 ? <p className="mt-3 text-sm text-warmgrey" data-testid="exceptions-none-open">All import issues are resolved or excluded.</p>
        : <ul className="mt-3">{shown.map((e) => <ExceptionRow key={e.id} e={e} onAssign={received ? null : setAssign} onExclude={setExcl} />)}</ul>}
      <AssignDialog e={assign} onClose={() => setAssign(null)} onDone={done} />
      <ExcludeDialog e={excl} onClose={() => setExcl(null)} onDone={done} />
    </section>
  );
};

// ID-087: receiving readiness inside the existing Receive dialog (the server re-checks on submit)
export const ReceivingReadiness = ({ r, ack, setAck, onReview }) => {
  if (!r) return null;
  if (r.state === "READY") return <p className="rounded-md border border-status-available/40 bg-status-available/5 p-3 text-sm text-charcoal" data-testid="receiving-ready">Ready to receive — no unresolved import issues.</p>;
  if (r.state === "BLOCKED") return (
    <div className="rounded-md border border-status-sold/40 bg-[#8C3A3A]/5 p-3 text-sm text-charcoal" data-testid="receiving-blocked">
      <p className="font-semibold text-status-sold">Receiving blocked</p>
      <p className="mt-1">{r.unresolved_rows ? `${r.unresolved_rows} unresolved import row${r.unresolved_rows === 1 ? "" : "s"} · ${r.unresolved_known_boxes} known boxes. Reconciliation is required before receiving.` : `Container is ${r.status.replace(/_/g, " ").toLowerCase()} — it must be departed or arriving.`}</p>
      {r.unresolved_rows > 0 && <Button size="sm" variant="outline" className="mt-2" onClick={onReview} data-testid="receiving-review-issues">Review issues</Button>}
    </div>
  );
  if (r.state !== "ACK_REQUIRED") return null;
  return (
    <div className="rounded-md border border-terracotta/40 bg-terracotta/5 p-3 text-sm text-charcoal" data-testid="receiving-ack-required">
      <p className="font-semibold">Excluded rows — acknowledgment required</p>
      <ul className="mt-1 text-xs">{r.excluded.map((x) => <li key={x.row}>Row {x.row} · “{x.sku_text || "—"}” · {x.boxes ?? "unknown"} boxes · {x.reason}</li>)}</ul>
      <label className="mt-2 flex items-start gap-2 text-xs">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" data-testid="receiving-ack-checkbox" />
        I acknowledge these {r.excluded_known_boxes} excluded boxes are not included in this container's accounted inventory and are not certified as received.
      </label>
    </div>
  );
};

// ID-086 RC-086.7: rows without a container number — link to an existing container after review (never guessed)
export const UnassignedImportIssues = ({ containers, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [pick, setPick] = useState({});
  const [excl, setExcl] = useState(null);
  const load = () => api.get("/container-import-exceptions/unassigned").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (!rows.length) return null;
  const link = async (e) => {
    try { await api.post(`/container-import-exceptions/${e.id}/link-container`, { container_id: pick[e.id] }); toast.success(`Row ${e.source_row} linked`); load(); onChanged?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <section className="mt-8 rounded-md border border-border bg-white p-5" data-testid="unassigned-import-issues">
      <h2 className="font-display text-xl font-medium text-charcoal">Unassigned import issues</h2>
      <p className="text-xs text-warmgrey">Packing-list rows with no container number. Link each to the correct container before resolving it.</p>
      <ul className="mt-2">{rows.map((e) => (
        <ExceptionRow key={e.id} e={e} onExclude={setExcl} extra={<>
          <select value={pick[e.id] || ""} onChange={(x) => setPick((p) => ({ ...p, [e.id]: x.target.value }))} className="h-8 max-w-[11rem] rounded-md border border-border bg-white px-2 text-xs" data-testid={`unassigned-select-${e.source_row}`}>
            <option value="">Choose container…</option>{containers.map((c) => <option key={c.id} value={c.id}>{c.zc_ref || c.container_number}</option>)}
          </select>
          <Button size="sm" variant="outline" disabled={!pick[e.id]} onClick={() => link(e)} data-testid={`unassigned-link-${e.source_row}`}>Link</Button></>} />
      ))}</ul>
      <ExcludeDialog e={excl} onClose={() => setExcl(null)} onDone={() => { setExcl(null); load(); }} />
    </section>
  );
};
